/**
 * Mint Route - Dynamic Embedded Dapp Page
 *
 * Route: /mint/:dappId
 *
 * This route renders a minting dapp embedded in the page.
 * Looks up the dapp by ID from the registry and renders it using
 * the EmbeddedMintingDapp component.
 *
 * Supports both internal (direct render) and external (iframe) dapps.
 *
 * @module routes/mint/$dappId
 * @category Routes
 */

import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { EmbeddedMintingDapp } from '@/minting/components/EmbeddedMintingDapp';
import { useMintingDapp } from '@/minting/hooks/useMintingDapp';
import '@/minting/components/IframeLoader'; // Ensure component styles are loaded
import './mint.css'; // Import mint-specific styles

// Route definition using TanStack Router
export const Route = createFileRoute('/$locale/mint/$dappId')({
  component: MintDappPage,
});

/**
 * MintDappPage - Page component for embedding minting dapps.
 *
 * Displays:
 * - Navbar at the top
 * - Card with dapp header and embedded component
 * - Loading/error states if dapp not found
 *
 * The EmbeddedMintingDapp component handles both internal and external dapps.
 */
function MintDappPage() {
  // Get the dapp ID from route params
  const { dappId } = Route.useParams();

  // Look up dapp from registry
  const dapp = useMintingDapp(dappId);

  return (
    <>
      <div
        className="main-content position-relative bg-gradient-dark"
        style={{
          minHeight: 'calc(100vh - 56px)',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        }}
      >
        <div className="container-fluid py-4" style={{ height: 'calc(100vh - 120px)' }}>
          {dapp ? (
            <div
              className="card border-radius-xl overflow-hidden h-100 shadow-dark"
              style={{
                backgroundColor: 'rgba(30, 30, 46, 0.8)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              {/* Card Header */}
              <div
                className="card-header bg-transparent pb-3"
                style={{
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <div className="d-flex align-items-center">
                  <div
                    className="icon icon-shape icon-xs me-3"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      background: 'rgba(139, 92, 246, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#8b5cf6',
                    }}
                  >
                    <i className="fas fa-hammer"></i>
                  </div>
                  <div>
                    <h3 className="text-light mb-0 h5">{dapp.name}</h3>
                    {dapp.description && (
                      <p className="text-light opacity-7 small mb-0 mt-1">
                        {dapp.description}
                      </p>
                    )}
                    <small className="text-light opacity-5 small">
                      {dapp.type} • {dapp.id}
                    </small>
                  </div>
                </div>
              </div>

              {/* Card Body - Embedded Dapp */}
              <div
                className="card-body p-0"
                style={{
                  height: 'calc(100% - 70px)',
                  position: 'relative',
                }}
              >
                <EmbeddedMintingDapp
                  dappId={dappId}
                  className="h-100 w-100"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                  }}
                />
              </div>

              {/* Footer with metadata */}
              {dapp.version && (
                <div
                  className="card-footer bg-transparent pt-2 pb-2"
                  style={{
                    borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                    fontSize: '0.75rem',
                  }}
                >
                  <div className="d-flex justify-content-between text-light opacity-6">
                    <span>v{dapp.version}</span>
                    {dapp.author && <span>by {dapp.author}</span>}
                    {(() => {
                      let tagsArray: string[] = [];
                      if (Array.isArray(dapp.tags)) {
                        tagsArray = dapp.tags;
                      } else if (typeof dapp.tags === 'string') {
                        try {
                          const parsed = JSON.parse(dapp.tags);
                          if (Array.isArray(parsed)) tagsArray = parsed;
                        } catch (e) {
                          console.warn('Failed to parse tags JSON:', e);
                        }
                      }
                      return tagsArray.length > 0 && (
                        <span className="text-end">
                          {tagsArray.slice(0, 3).join(', ')}
                          {tagsArray.length > 3 && '...'}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Dapp not found state
            (<div
              className="card border-radius-xl h-100"
              style={{
                backgroundColor: 'rgba(30, 30, 46, 0.8)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <div className="card-body text-center py-5">
                <i
                  className="fas fa-exclamation-triangle fa-4x text-warning mb-4"
                  aria-hidden="true"
                ></i>
                <h4 className="text-light mb-3">Minting Dapp Not Found</h4>
                <p className="text-light opacity-6 mb-4">
                  The application "{dappId}" is not registered or may have been removed.
                  Please check the URL and try again.
                </p>
                <div className="mt-4">
                  <a
                    href="/"
                    className="btn btn-primary me-2"
                    style={{ borderRadius: '8px' }}
                  >
                    <i className="fas fa-home me-2"></i>
                    Go Home
                  </a>
                  <button
                    onClick={() => window.history.back()}
                    className="btn btn-outline-secondary"
                    style={{ borderRadius: '8px' }}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Go Back
                  </button>
                </div>
              </div>
            </div>)
          )}
        </div>
      </div>
    </>
  )
}
